import { GutachtenAnsicht, ProbenAnsicht } from '@/api/model';
import type { breadcrumb } from '@/composables/Breadcrumbs';
import { ROUTE_NAMES } from '@/router/routeNames';
import { Probentyp, ProbentypHelpers } from '@/types/probentyp';
import { getFormatiertesDatum } from './i18n';

function probenlisteBreadcrumb(probentyp: Probentyp): breadcrumb {
    switch (probentyp) {
        case Probentyp.Probe:
            return { sprachcode: '[Breadcrumb]Proben', routeName: ROUTE_NAMES.PROBEN.LISTE };
        case Probentyp.Mustervorlage:
            return { sprachcode: '[Breadcrumb]Mustervorlagen', routeName: ROUTE_NAMES.MUSTERVORLAGEN.LISTE };
        default:
            return ProbentypHelpers.throwUnbekannterProbentypError(probentyp);
    }
}

function probenEditBreadcrumb(probentyp: Probentyp): breadcrumb {
    switch (probentyp) {
        case Probentyp.Probe:
            return { sprachcode: '[Breadcrumb]ProbeErfassen' };
        case Probentyp.Mustervorlage:
            return { sprachcode: '[Breadcrumb]VorlageErfassen' };
        default:
            return ProbentypHelpers.throwUnbekannterProbentypError(probentyp);
    }
}

function textvorlagenBreadcrumb(): breadcrumb {
    return { sprachcode: '[Breadcrumb]Textvorlagen' };
}

function etikettendruckBreadcrumb(): breadcrumb {
    return { sprachcode: '[Breadcrumb]Etikettendruck', routeName: ROUTE_NAMES.ETIKETTEN.LISTE };
}

function probenbegleitscheinDruckBreadcrumb(): breadcrumb {
    return { sprachcode: '[Breadcrumb]ProbenbegleitscheinDruck', routeName: ROUTE_NAMES.PROBENBEGLEITSCHEIN.LISTE };
}

function excelImportBreadcumb(): breadcrumb {
    return { sprachcode: '[Breadcrumb]ProbenImportAusExcel' };
}

function ergebnisseTypBreadcrumb(ansicht: ProbenAnsicht): breadcrumb {
    switch (ansicht) {
        case ProbenAnsicht.Angemeldete:
            return { sprachcode: '[Breadcrumb]Angemeldete', routeName: ROUTE_NAMES.ERGEBNISSE.ANGEMELDETE };
        case ProbenAnsicht.ImLabor:
            return { sprachcode: '[Breadcrumb]ImLabor', routeName: ROUTE_NAMES.ERGEBNISSE.IM_LABOR };
        case ProbenAnsicht.VorlaufigeErgebnisse:
            return { sprachcode: '[Breadcrumb]VorlaufigeErgebnisse', routeName: ROUTE_NAMES.ERGEBNISSE.VORLAEUFIGE };
        case ProbenAnsicht.EndgueltigeErgebnisse:
            return { sprachcode: '[Breadcrumb]EndgueltigeErgebnisse', routeName: ROUTE_NAMES.ERGEBNISSE.ENDGUELTIGE };
        default: {
            throw new Error(`Unbekannte ProbenAnsicht: ${ansicht}`);
        }
    }
}

export function getHomeBreadcrumb(): breadcrumb {
    return { sprachcode: '[Breadcrumb]MyBAV', routeName: ROUTE_NAMES.DASHBOARD, icon: 'pi pi-home' };
}

// Erfassen > Proben | Erfassen > Mustervorlagen
export function getProbenlisteBreadcrumbs(probentyp: Probentyp): breadcrumb[] {
    return [probenlisteBreadcrumb(probentyp)];
}

// Erfassen > Proben | Mustervorlagen > Probe/Vorlage erfassen
export function getProbenErfassenBreadcrumbs(probentyp: Probentyp): breadcrumb[] {
    return [probenlisteBreadcrumb(probentyp), probenEditBreadcrumb(probentyp)];
}

export function getSchnellsucheProbenErfassenBreadcrumbs(probentyp: Probentyp, suchtext: string): breadcrumb[] {
    return [...getSchnellsucheBreadcrumbs(suchtext), probenEditBreadcrumb(probentyp)];
}

export function getProbenErfassenMischprobeBreadcrumbs(probentyp: Probentyp): breadcrumb[] {
    return [probenlisteBreadcrumb(probentyp), { sprachcode: '[Breadcrumb]Mischprobe' }];
}

export function getProbenErfassenTestzeitpunktBreadcrumbs(probentyp: Probentyp): breadcrumb[] {
    return [probenlisteBreadcrumb(probentyp), { sprachcode: '[Breadcrumb]Testzeitpunkt' }];
}

export function getProbenNrZuordnenBreadcrumbs(): breadcrumb[] {
    return [probenlisteBreadcrumb(Probentyp.Probe), { sprachcode: '[Breadcrumb]ProbenNrZuordnen' }];
}

export function getPruefplaeneBreadcrumbs(probentyp: Probentyp): breadcrumb[] {
    return [probenlisteBreadcrumb(probentyp), { sprachcode: '[Breadcrumb]Pruefplaene' }];
}

export function getTextvorlagenBreadcrumbs(probentyp: Probentyp): breadcrumb[] {
    return [probenlisteBreadcrumb(probentyp), textvorlagenBreadcrumb()];
}

// Erfassen > Etiketten drucken
export function getEtikettendruckBreadcrumbs(): breadcrumb[] {
    return [etikettendruckBreadcrumb()];
}

export function getEtikettendruckFilterBreadcrumbs(): breadcrumb[] {
    return [etikettendruckBreadcrumb(), { sprachcode: '[Breadcrumb]EtikettendruckFilter' }];
}

// Erfassen > Probenbegleitschein drucken
export function getProbenbegleitscheinDruckBreadcrumbs(): breadcrumb[] {
    return [probenbegleitscheinDruckBreadcrumb()];
}

export function getProbenbegleitscheinDruckFilterBreadcrumbs(): breadcrumb[] {
    return [probenbegleitscheinDruckBreadcrumb(), { sprachcode: '[Breadcrumb]ProbenbegleitscheinDruckFilter' }];
}

export function getMustervorlagenFilterBreadcrumbs(): breadcrumb[] {
    return [probenlisteBreadcrumb(Probentyp.Mustervorlage), { sprachcode: '[Breadcrumb]MustervorlagenFilter' }];
}

// Erfassen > Proben aus Excel-Vorlage importieren
export function getExcelImportBreadcrumbs(): breadcrumb[] {
    return [excelImportBreadcumb()];
}

// Ergebnisse > (Angemeldete | Im Labor | Vorläufige Ergebnisse | Endgültige Ergebnisse)
export function getErgebnisseBreadcrumbs(ansicht: ProbenAnsicht): breadcrumb[] {
    return [ergebnisseTypBreadcrumb(ansicht)];
}

// Ergebnisse > (Angemeldete | Im Labor | Vorläufige Ergebnisse | Endgültige Ergebnisse) > Filter
export function getErgebnisseFilterBreadcrumbs(ansicht: ProbenAnsicht): breadcrumb[] {
    return [ergebnisseTypBreadcrumb(ansicht), { sprachcode: '[Breadcrumb]ErgebnisseFilter' }];
}

// Ergebnisse > (Im Labor | Vorläufige Ergebnisse | Endgültige Ergebnisse) > Ergebnis Probe {probenNr}
export function getErgebnissDetailBreadcrumbs(ansicht: ProbenAnsicht, probenNr: number): breadcrumb[] {
    return [...getErgebnisseBreadcrumbs(ansicht), { sprachcode: '[Breadcrumb]ErgebnisProbe', sprachcodeParams: { probenNr } }];
}

// Ergebnisse > Statistik
export function getErgebnisseStatistikBreadcrumbs(): breadcrumb[] {
    return [{ sprachcode: '[Breadcrumb]Ergebnisstatistik', routeName: ROUTE_NAMES.ERGEBNISSE.STATISTIK }];
}

export function getSchnellsucheBreadcrumbs(suchtext?: string): breadcrumb[] {
    return [
        {
            sprachcode: '[Breadcrumb]Suche',
            routeName: ROUTE_NAMES.ERGEBNISSE.SCHNELLSUCHE,
            routeQuery: suchtext ? { suchtext } : undefined
        }
    ];
}

export function getSchnellsucheDetailBreadcrumbs(probenNr: number, suchtext: string): breadcrumb[] {
    return [...getSchnellsucheBreadcrumbs(suchtext), { sprachcode: '[Breadcrumb]ErgebnisProbe', sprachcodeParams: { probenNr } }];
}

// Begehungsberichte
export function getBegehungsberichteBreadcrumbs(): breadcrumb[] {
    return [{ sprachcode: '[Breadcrumb]Begehungsberichte', routeName: ROUTE_NAMES.BEGEHUNGSBERICHTE.LISTE }];
}

// Begehungsberichte > Begehungsbericht
export function getBegehungDetailBreadcrumbs(datum?: Date): breadcrumb[] {
    return [...getBegehungsberichteBreadcrumbs(), { sprachcode: '[Breadcrumb]Begehungsbericht', sprachcodeParams: { datum: getFormatiertesDatum(datum) } }];
}

// Qualitaetskontrollzertifikate
export function getQualitaetskontrollzertifikateBreadcrumbs(): breadcrumb[] {
    return [{ sprachcode: '[Breadcrumb]Qualitaetskontrollzertifikate', routeName: ROUTE_NAMES.ZERTIFIKATE.QUALITAETSKONTROLLE }];
}

// Wissensdatenbank
export function getWissensdatenbankBreadcrumbs(): breadcrumb[] {
    return [{ sprachcode: '[Breadcrumb]Wissensdatenbank', routeName: ROUTE_NAMES.WISSENSDATENBANK.UEBERSICHT }];
}

// Gutachten
export function getGutachtenErfassenBreadcrumbs(): breadcrumb[] {
    return [{ sprachcode: '[Breadcrumb]GutachtenErfassen', routeName: ROUTE_NAMES.GUTACHTEN.ERFASSEN }];
}

export function getGutachtenTypAuswahlBreadcrumbs(): breadcrumb[] {
    return [...getGutachtenErfassenBreadcrumbs(), { sprachcode: '[Breadcrumb]GutachtenTypAuswahl' }];
}

export function getGutachtenBearbeitenBreadcrumbs(): breadcrumb[] {
    return [...getGutachtenErfassenBreadcrumbs(), { sprachcode: '[Breadcrumb]GutachtenBearbeiten' }];
}

export function getSchnellsucheGutachtenBearbeitenBreadcrumbs(suchtext: string): breadcrumb[] {
    return [...getSchnellsucheBreadcrumbs(suchtext), { sprachcode: '[Breadcrumb]GutachtenBearbeiten' }];
}

function gutachtenAnsichtBreadcrumb(ansicht: GutachtenAnsicht): breadcrumb {
    switch (ansicht) {
        case GutachtenAnsicht.Ergebnisse:
            return { sprachcode: '[Breadcrumb]GutachtenErgebnisse', routeName: ROUTE_NAMES.GUTACHTEN.ERGEBNISSE };
        case GutachtenAnsicht.InBearbeitung:
            return { sprachcode: '[Breadcrumb]GutachtenInBearbeitung', routeName: ROUTE_NAMES.GUTACHTEN.IN_BEARBEITUNG };
        default:
            throw new Error(`Unbekannte Gutachten-Ansicht für Breadcrumbs: ${ansicht}`);
    }
}

export function getGutachtenBreadcrumbs(ansicht: GutachtenAnsicht): breadcrumb[] {
    return [gutachtenAnsichtBreadcrumb(ansicht)];
}

export function getGutachtenErgebnisseBreadcrumbs(): breadcrumb[] {
    return getGutachtenBreadcrumbs(GutachtenAnsicht.Ergebnisse);
}

export function getGutachtenRohstoffspezifikationenBreadcrumbs(): breadcrumb[] {
    return [{ sprachcode: '[Breadcrumb]GutachtenRohstoffspezifikationen', routeName: ROUTE_NAMES.GUTACHTEN.ROHSTOFFSPEZIFIKATIONEN }];
}

export function getGutachtenErgebnisseFilterBreadcrumbs(): breadcrumb[] {
    return [...getGutachtenErgebnisseBreadcrumbs(), { sprachcode: '[Breadcrumb]GutachtenErgebnisseFilter' }];
}

export function getGutachtenDetailBreadcrumbs(ansicht: GutachtenAnsicht, id: string): breadcrumb[] {
    return [...getGutachtenBreadcrumbs(ansicht), { sprachcode: '[Breadcrumb]GutachtenErgebnis', sprachcodeParams: { id } }];
}

export function getSchnellsucheGutachtenDetailBreadcrumbs(id: string, suchtext: string): breadcrumb[] {
    return [...getSchnellsucheBreadcrumbs(suchtext), { sprachcode: '[Breadcrumb]GutachtenErgebnis', sprachcodeParams: { id } }];
}

// Kundenbereich
export function getKundenbereichBreadcrumbs(): breadcrumb[] {
    return [{ sprachcode: '[Breadcrumb]Kundenbereich', routeName: ROUTE_NAMES.KUNDENBEREICH }];
}
